import { watch } from 'vue';
import { useNowTick } from '@/stores/nowTick.store';
import { useSessionStore } from '@/stores/session.store';
import { resolveMine } from '@/api/tick';

/**
 * Tier 2 temps-réel — déclencheur « à la seconde ». Un holder fournit une
 * fonction `getNextDueMs()` qui renvoie le finishAt (ms epoch) de SA tâche en
 * cours la plus proche (ou null si rien). Dès que `nowTick` dépasse cette date
 * (+ une petite grâce d'horloge), on appelle `resolve-mine` UNE fois pour cette
 * échéance → le serveur résout, pousse (SignalR), le holder se recharge.
 *
 * - Coalescing global : un seul appel en vol à la fois (in-flight + débounce),
 *   donc plusieurs holders/échéances simultanées = un seul aller-retour.
 * - Anti-spam : on ne refire pas pour la même échéance tant qu'elle n'a pas
 *   changé (le push fera évoluer la liste -> nouveau getNextDueMs).
 * - À utiliser dans un composant monté (le watch s'auto-nettoie au unmount).
 */

const GRACE_MS = 600;           // tolérance de skew front/serveur
const MIN_INTERVAL_MS = 1500;   // débounce global entre 2 appels

// État partagé (module-level) pour coalescer entre tous les holders.
let inFlight = false;
let lastCallAt = 0;

async function fireResolve(): Promise<void> {
  const now = Date.now();
  if (inFlight || now - lastCallAt < MIN_INTERVAL_MS) return;
  const session = useSessionStore();
  const pu = session.activePlayerUniverseId ?? 0;
  if (!pu) return;
  inFlight = true;
  lastCallAt = now;
  try {
    await resolveMine(pu);
  } catch { /* best-effort : le tick 10s reste le filet */ } finally {
    inFlight = false;
  }
}

export function useDueResolveTrigger(getNextDueMs: () => number | null): void {
  const { now } = useNowTick().useTick();
  let firedForDue: number | null = null;

  watch(now, (ms) => {
    const due = getNextDueMs();
    if (due == null) { firedForDue = null; return; }
    if (ms >= due + GRACE_MS && firedForDue !== due) {
      firedForDue = due;            // marque cette échéance comme tirée
      void fireResolve();
    }
  });
}
